import * as THREE from 'three';
import {
    activateRagdoll,
    deactivateRagdoll,
    createDragConstraint,
    updateDragConstraint,
    removeDragConstraint,
} from './ragdollSystem';

export const createInputHandlers = ({
    camera,
    renderer,
    characterInstances,
    cameraController,
    world,
}) => {
    const raycaster  = new THREE.Raycaster();
    const pointer    = new THREE.Vector2();
    const dragPlane  = new THREE.Plane();

    let draggedInst          = null;
    let dragData             = null; // { cursorBody, grabConstraint, grabbedBody }
    let isRightMouseRotating = false;
    let lastPointerX         = 0;
    let lastPointerY         = 0;

    // ── Helpers ──────────────────────────────────────────────
    const getNDC = (event) => {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x  = ((event.clientX - rect.left) / rect.width)  *  2 - 1;
        pointer.y  = -((event.clientY - rect.top)  / rect.height) * 2 + 1;
    };

    const updateDragPlane = (worldPos) => {
        const cameraForward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
        dragPlane.setFromNormalAndCoplanarPoint(cameraForward.negate(), worldPos);
    };

    // ── Pointer down ─────────────────────────────────────────
    const onPointerDown = (event) => {
        if (event.button === 2) {
            isRightMouseRotating = true;
            lastPointerX = event.clientX;
            lastPointerY = event.clientY;
            event.preventDefault();
            return;
        }
        if (event.button !== 0) return;

        getNDC(event);
        raycaster.setFromCamera(pointer, camera);

        // Collect all character mesh children for raycasting
        const hitList = [];
        for (const inst of characterInstances) {
            inst.model.traverse(c => { if (c.isMesh) hitList.push(c); });
        }
        const intersects = raycaster.intersectObjects(hitList, false);
        if (intersects.length === 0) return;

        // Find which character instance was hit
        let hitObj   = intersects[0].object;
        let targetInst = null;
        while (hitObj && !targetInst) {
            const cur = hitObj;
            targetInst = characterInstances.find(i => i.model === cur);
            hitObj = hitObj.parent;
        }
        if (!targetInst) return;

        const hitPoint = intersects[0].point;

        // Cancel any pending animation resume
        if (targetInst.resumeAnimTimer) {
            clearTimeout(targetInst.resumeAnimTimer);
            targetInst.resumeAnimTimer = null;
        }

        // Activate ragdoll physics on this character
        activateRagdoll(targetInst, world);
        targetInst.isBeingDragged    = true;
        targetInst.isAnimationPaused = true;

        // Build drag plane through the hit point, perpendicular to camera
        updateDragPlane(hitPoint.clone());

        // Create physics mouse constraint on closest joint
        dragData    = createDragConstraint(world, targetInst, hitPoint);
        draggedInst = targetInst;

        event.preventDefault();
    };

    // ── Pointer move ─────────────────────────────────────────
    const onPointerMove = (event) => {
        if (isRightMouseRotating && (event.buttons & 2) !== 0) {
            cameraController.applyMouseRotationDelta(
                event.clientX - lastPointerX,
                event.clientY - lastPointerY
            );
            lastPointerX = event.clientX;
            lastPointerY = event.clientY;
            event.preventDefault();
            return;
        }

        if (!draggedInst || !dragData) return;

        getNDC(event);
        raycaster.setFromCamera(pointer, camera);

        // Keep drag plane facing camera
        const cameraForward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
        dragPlane.normal.copy(cameraForward).negate();

        const worldPoint = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(dragPlane, worldPoint)) {
            updateDragConstraint(dragData, worldPoint);
        }
        event.preventDefault();
    };

    // ── Pointer up ───────────────────────────────────────────
    const onPointerUp = (event) => {
        if (isRightMouseRotating) isRightMouseRotating = false;

        if (!draggedInst) return;

        const endedInst = draggedInst;
        draggedInst = null;
        endedInst.isBeingDragged = false;

        // Remove the mouse constraint so physics can settle freely
        removeDragConstraint(world, dragData);
        dragData = null;

        // After 5 s: deactivate ragdoll and resume animations
        endedInst.resumeAnimTimer = setTimeout(() => {
            deactivateRagdoll(endedInst, world);
            endedInst.isAnimationPaused  = false;
            endedInst.resumeAnimTimer    = null;
            // Re-sync root-motion tracking after re-entering animation mode
            if (endedInst.rootBone) {
                endedInst.lastRootAnimPos.set(0, 0, 0);
            }
        }, 5000);

        event.preventDefault();
    };

    // ── DOM attachment ────────────────────────────────────────
    const onContextMenu = (e) => e.preventDefault();

    const attach = () => {
        renderer.domElement.addEventListener('contextmenu', onContextMenu);
        renderer.domElement.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('pointermove',   onPointerMove);
        window.addEventListener('pointerup',     onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
        window.addEventListener('blur',          onPointerUp);
    };

    const detach = () => {
        renderer.domElement.removeEventListener('contextmenu', onContextMenu);
        renderer.domElement.removeEventListener('pointerdown', onPointerDown);
        window.removeEventListener('pointermove',   onPointerMove);
        window.removeEventListener('pointerup',     onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);
        window.removeEventListener('blur',          onPointerUp);
    };

    return { attach, detach };
};
